'use strict';

var _ = require('underscore');
var ActionTypes = require('../createStore');
var isPlainObject = require('./isPlainObject');
var console = window.console;
/* eslint-disable no-console */

function getUndefinedStateErrorMessage(key, action) {
  var actionType = action && action.type;
  var actionName = actionType && ('"' + actionType.toString() + '"') || 'an action';

  return (
		'Reducer "' + key + '" returned undefined handling ' + actionName + 
		'. To ignore an action, you must explicitly return the previous state.'
	);
}

function getUnexpectedStateKeyWarningMessage(inputState, outputState, action) {
  var reducerKeys = _.keys(outputState);
  var argumentName = action && action.type === ActionTypes.INIT ? 'initialState argument passed to createStore' : 'previous state received by the reducer';

  if (reducerKeys.length === 0) {
    return (
      'Store does not have a valid reducer. Make sure the argument passed ' +
      'to combineReducers is an object whose values are reducers.'
    );
  }

  if (!isPlainObject(inputState)) {
    return (
      'The ' + argumentName + ' has unexpected type of "' +
      ({}).toString.call(inputState).match(/\s([a-z|A-Z]+)/)[1] +
      '". Expected argument to be an object with the following ' +
      'keys: "' + reducerKeys.join('", "') + '"'
    );
  }

  var unexpectedKeys = _.filter(_.keys(inputState), function unexpectedKeysPred(key) {
    return reducerKeys.indexOf(key) < 0;
  });

  if (unexpectedKeys.length > 0) {
    return (
      'Unexpected ' + (unexpectedKeys.length > 1 ? 'keys' : 'key') + ' ' +
      '"' + unexpectedKeys.join('", "') + '" found in ' + argumentName + '. ' +
      'Expected to find one of the known reducer keys instead: ' +
      '"' + reducerKeys.join('", "') + '". Unexpected keys will be ignored.'
    );
  }
}

function assertReducerSanity(reducers) {
  _.each(_.keys(reducers), function (key) {
    var reducer = reducers[key];
    var initialState = reducer(undefined, { type: ActionTypes.INIT });

    if (_.isUndefined(initialState)) {
      throw new Error(
        'Reducer "' + key + '" returned undefined during initialization. ' +
        'If the state passed to the reducer is undefined, you must ' +
        'explicitly return the initial state. The initial state may ' +
        'not be undefined.'
      );
    }

    var type = '@@redux/PROBE_UNKNOWN_ACTION_' + Math.random().toString(36).substring(7).split('').join('.');
    if (_.isUndefined(reducer(undefined, { type: type }))) {
      throw new Error(
        'Reducer "' + key + '" returned undefined when probed with a random type. ' +
        'Don\'t try to handle ' + ActionTypes.INIT + ' or other actions in "redux/*" ' +
        'namespace. They are considered private. Instead, you must return the ' +
        'current state for any unknown actions, unless it is undefined, ' +
        'in which case you must return the initial state, regardless of the ' +
        'action type. The initial state may not be undefined.'
      );
    }
  });
}

/**
 * Turns an object whose values are different reducer functions, into a single
 * reducer function. It will call every child reducer, and gather their results
 * into a single state object, whose keys correspond to the keys of the passed
 * reducer functions.
 *
 * @param {Object} reducers An object whose values correspond to different
 * reducer functions that need to be combined into one. One handy way to obtain
 * it is to use ES6 `import * as reducers` syntax. The reducers may never return
 * undefined for any action. Instead, they should return their initial state
 * if the state passed to them was undefined, and the current state for any
 * unrecognized action.
 *
 * @returns {Function} A reducer function that invokes every reducer inside the
 * passed object, and builds a state object with the same shape.
 */

module.exports = function combineReducers(reducers) {
  var finalReducers = _.pick(reducers, _.isFunction);
  var sanityError;

  try {
    assertReducerSanity(finalReducers);
  } catch (e) {
    sanityError = e;
  }

  var defaultState = _.mapObject(finalReducers, function () {});

  return function combination(state, action) {
  	state = _.isUndefined(state) ? defaultState : state;
    if (sanityError) {
      throw sanityError;
    }

    var hasChanged = false;
    var finalState = _.mapObject(finalReducers, function (reducer, key) {
      var previousStateForKey = state[key];
      var nextStateForKey = reducer(previousStateForKey, action);
      if (_.isUndefined(nextStateForKey)) {
        var errorMessage = getUndefinedStateErrorMessage(key, action);
        throw new Error(errorMessage);
      }
      hasChanged = hasChanged || nextStateForKey !== previousStateForKey;
      return nextStateForKey;
    });

    if (process.env.NODE_ENV !== 'production') {
      var warningMessage = getUnexpectedStateKeyWarningMessage(state, finalState, action);
      if (warningMessage) {
        console.error(warningMessage);
      }
    }

    return hasChanged ? finalState : state;
  };
};
